/**
 * School page content adapter.
 *
 * What: Generates all structured content for a SchoolMathPage from a SchoolRecord + variant.
 * Why: Keeps template logic out of the component and makes content testable in isolation.
 * When: Update when adding new variants, changing copy, or adding override support.
 */
import { CUT_OFF_S1_INTAKE_YEAR } from '@shared/schools/schoolCutOffPoints.js';
import type { SchoolRecord } from '@shared/schools/schoolData.js';
import type { SchoolHeritage } from '@shared/schools/schoolHeritage.js';
import type { SchoolVariant } from '@shared/schools/schoolVariants.js';

import {
  CLASS_SIZE_FAQ,
  CLASS_SIZE_VALUE,
  MAX_CLASS_SIZE,
} from './classSizeContent.js';
import {
  buildCutOffMetaSummary,
  buildSchoolCutOffContent,
  getHeadlineCutOff,
  getSchoolCutOffPoints,
  type SchoolCutOffContent,
} from './schoolCutOffContent.js';
import { applySchoolOverride, getSchoolOverride } from './schoolOverrides.js';

export interface SchoolFaqItem {
  question: string;
  answer: string;
}

export interface SchoolRelatedLink {
  label: string;
  path: string;
  description: string;
}

export interface SchoolProgramFact {
  label: string;
  value: string;
  detail?: string;
}

export interface SchoolAboutContent {
  heading: string;
  body: string;
  facts: { label: string; value: string }[];
}

export interface SchoolPageContent {
  metaTitle: string;
  metaDescription: string;
  heroHeading: string;
  heroSubheading: string;
  levelBadge: string;
  supportHeading: string;
  supportBullets: string[];
  programFacts: {
    eyebrow: string;
    title: string;
    body: string;
    facts: SchoolProgramFact[];
  };
  relatedLinks: {
    eyebrow: string;
    title: string;
    links: SchoolRelatedLink[];
  };
  faqItems: SchoolFaqItem[];
  /** Prose intro paragraph — present only when reviewed heritage data exists. */
  introText?: string;
  /** "About [School]" section — present only when reviewed heritage data exists. */
  aboutSchool?: SchoolAboutContent;
  /**
   * PSLE cut-off section — present only for schools MOE publishes a cut-off
   * point for. Absent means "MOE published nothing", and the page then renders
   * no section rather than an empty state or a guess.
   */
  cutOffPoints?: SchoolCutOffContent;
}

const BRAND = 'DeepThink';

/**
 * Append the abbreviation as an inline parenthetical, e.g.
 * "Maris Stella High School" + "MSHS" → "Maris Stella High School (MSHS)".
 *
 * Used only in the H1, meta title, and meta description — not in body copy,
 * subheadings, support bullets, FAQ text, or related sections (where it would
 * read as repetitive). Returns the bare name when no abbreviation is set.
 */
function withAbbr(school: SchoolRecord): string {
  return school.abbreviation
    ? `${school.displayName} (${school.abbreviation})`
    : school.displayName;
}

// --- Variant content templates ---

function primaryContent(
  school: SchoolRecord
): Omit<SchoolPageContent, 'metaTitle' | 'metaDescription'> {
  const name = school.displayName;
  return {
    heroHeading: `Math Tuition for ${withAbbr(school)} Students`,
    heroSubheading: `Primary Math classes capped at ${MAX_CLASS_SIZE} students, grouped by learning pace — so every child gets individual attention, and the skills to show it in exams.`,
    levelBadge: 'Primary',
    supportHeading: `How ${BRAND} supports ${name} students`,
    supportBullets: [
      `Lessons follow the MOE Primary Math syllabus exactly — the same topics, heuristics, and model-drawing methods your child uses at ${name}`,
      `Classes capped at ${MAX_CLASS_SIZE} students and grouped by learning pace, so your child can ask questions, get feedback, and is never lost in the crowd`,
      `From P5 onwards, every class is structured around PSLE technique — not just content, but how to show working clearly and pick up method marks`,
      `Parents are kept in the loop on what was covered and where their child needs more practice — so you're never in the dark between sessions`,
      ...(school.giftedInd
        ? [
            `${name} has a Gifted Education Programme (GEP) — GEP students receive enrichment-level support that goes beyond the standard PSLE syllabus`,
          ]
        : []),
    ],
    programFacts: {
      eyebrow: 'The programme',
      title: 'What your child gets at DeepThink',
      body: `Each weekly session builds directly on the last. Your child works through new concepts, revisits gaps, and practises exam-style questions — in a group of no more than ${MAX_CLASS_SIZE}, small enough that the teacher actually knows them.`,
      facts: [
        {
          label: 'Class size',
          value: CLASS_SIZE_VALUE,
          detail:
            'Grouped by learning pace, so every child is taught at theirs',
        },
        { label: 'Weekly duration', value: '1.5 hours' },
        { label: 'Levels covered', value: 'P1 – P6' },
        { label: 'PSLE prep', value: 'Integrated from P5' },
      ],
    },
    relatedLinks: {
      eyebrow: 'Explore by level',
      title: 'Find the right class for your child',
      links: [
        {
          label: 'Primary School Math',
          path: '/primary-school',
          description: 'Overview of P1–P6 support at DeepThink',
        },
        {
          label: 'Primary 1',
          path: '/primary-1',
          description:
            'Number sense, basic operations, and early problem-solving',
        },
        {
          label: 'Primary 2',
          path: '/primary-2',
          description: 'Fractions, measurement, and word problems',
        },
        {
          label: 'Primary 3',
          path: '/primary-3',
          description: 'Geometry, multiplication, and division mastery',
        },
        {
          label: 'Primary 4',
          path: '/primary-4',
          description: 'Fractions, decimals, area, and perimeter',
        },
        {
          label: 'Primary 5',
          path: '/primary-5',
          description: 'Percentages, rate, and the start of PSLE prep',
        },
        {
          label: 'Primary 6',
          path: '/primary-6',
          description: 'Full PSLE preparation and exam technique',
        },
        {
          label: 'PSLE Math',
          path: '/psle',
          description: 'Targeted PSLE prep with past-year practice',
        },
      ],
    },
    faqItems: [
      {
        question: `Does ${BRAND} follow the same syllabus my child is on at ${name}?`,
        answer: `Yes. All Singapore primary schools follow the MOE Primary Math syllabus, and our P1–P6 classes are built around that curriculum — the same topics, the same model-drawing and heuristics approach, and the same exam format your child sits at ${name}.`,
      },
      {
        question: `How will I know if my child is making progress?`,
        answer: `Parents are kept informed on what was covered and where their child should focus. We also flag any persistent gaps as soon as we spot them — not just before exams.`,
      },
      {
        question: `When should my child start tuition?`,
        answer: `There's no single right answer, but earlier is generally better. Gaps in foundational topics like fractions and ratios compound as children move through primary school. A free trial class is a good way to find out where your child actually stands.`,
      },
      {
        question: `What makes ${BRAND} different from other math tuition centres?`,
        answer: `Classes capped at ${MAX_CLASS_SIZE} students and grouped by learning pace, parents kept in the loop on progress, and a curriculum focused on the exact skills MOE examiners test — including how to structure solutions, not just whether the answer is correct.`,
      },
    ],
  };
}

function secondaryContent(
  school: SchoolRecord
): Omit<SchoolPageContent, 'metaTitle' | 'metaDescription'> {
  const name = school.displayName;
  const has4Year = school.mainLevelCode === 'SECONDARY (S1-S4)';
  const topLevel = has4Year ? 'S4' : 'S5';

  return {
    heroHeading: `Math Tuition for ${withAbbr(school)} Students`,
    heroSubheading: `Secondary Math classes grouped by stream, each covering the exact syllabus and exam format for that level.`,
    levelBadge: 'Secondary',
    supportHeading: `How ${BRAND} supports ${name} students`,
    supportBullets: [
      `G1, G2, and G3 students are in separate classes, each covering the content and exam format for that stream`,
      `O-Level, N-Level, and SEC exam preparation built in from S3, matched to the stream your child is sitting`,
      `E-Math and A-Math taught in separate classes from S3 onwards`,
      `S1 to ${topLevel} coverage so your child can stay with ${BRAND} for their entire secondary journey without switching centres`,
    ],
    programFacts: {
      eyebrow: 'The programme',
      title: 'What your child gets at DeepThink',
      body: `Secondary Math is not one-size-fits-all. A G1 student preparing for the SEC exam needs completely different content from a G3 student heading for A-Math. We keep streams in separate classes so every session is relevant.`,
      facts: [
        {
          label: 'Class size',
          value: CLASS_SIZE_VALUE,
          detail: 'Stream-specific, and grouped by learning pace',
        },
        { label: 'Weekly duration', value: '1.5 hours' },
        { label: 'Levels covered', value: `S1 – ${topLevel}` },
        { label: 'Streams', value: 'G1, G2, G3 (separate classes)' },
      ],
    },
    relatedLinks: {
      eyebrow: 'Explore by level',
      title: 'Find the right class for your child',
      links: [
        {
          label: 'Secondary School Math',
          path: '/secondary-school',
          description: 'Overview of S1–S4/S5 support at DeepThink',
        },
        {
          label: 'Secondary 1',
          path: '/sec-1',
          description: 'Algebra foundations and geometric reasoning',
        },
        {
          label: 'Secondary 2',
          path: '/sec-2',
          description: 'Simultaneous equations, indices, and statistics',
        },
        {
          label: 'Secondary 3',
          path: '/sec-3',
          description: 'Stream-specific SEC exam and O-Level prep begins',
        },
        {
          label: 'Secondary 4',
          path: '/sec-4',
          description: 'Full O-Level, N-Level, and SEC exam preparation',
        },
        {
          label: 'O-Level / SEC Math',
          path: '/o-level',
          description: 'O-Level and SEC exam preparation',
        },
        {
          label: 'E-Math',
          path: '/e-math',
          description: 'Elementary Mathematics (G1, G2, G3)',
        },
        {
          label: 'A-Math',
          path: '/a-math',
          description: 'Additional Mathematics for G2 and G3 students',
        },
        {
          label: 'N-Level Math',
          path: '/n-level',
          description: 'N(A) and N(T) targeted support',
        },
      ],
    },
    faqItems: [
      {
        question: `Does my child's stream affect which class they join at ${BRAND}?`,
        answer: `Yes. G1, G2, and G3 students each have their own class, so every lesson matches the exact syllabus and exam format your child is sitting.`,
      },
      {
        question: `My child is doing okay in S1/S2 but I'm worried about the jump to S3. Is it too early to start?`,
        answer: `S3 is where most students hit a wall — the syllabus gets significantly harder, A-Math is new, and SEC exam (or O-Level for the current Sec 4 cohort) prep begins. Students who build strong foundations in S1–S2 handle that jump far better. Starting early is the highest-leverage move a parent can make.`,
      },
      {
        question: `When is the right time for a secondary student to start tuition?`,
        answer: `S1 is ideal — algebra gaps from the start of secondary compound quickly and are harder to fix later. That said, students who join in S3 or S4 can still benefit significantly from targeted exam preparation.`,
      },
      {
        question: `What makes ${BRAND} different for secondary students?`,
        answer: `Stream-specific classes. Every lesson, worked example, and past-year paper is matched to your child's stream.`,
      },
    ],
  };
}

function ipContent(
  school: SchoolRecord
): Omit<SchoolPageContent, 'metaTitle' | 'metaDescription'> {
  const name = school.displayName;

  return {
    heroHeading: `IP Math Tuition for ${withAbbr(school)} Students`,
    heroSubheading: `Dedicated Integrated Programme Math classes covering the accelerated curriculum your child sits.`,
    levelBadge: 'IP',
    supportHeading: `How ${BRAND} supports ${name} IP students`,
    supportBullets: [
      `Dedicated IP class covering the accelerated track, including topics not in the standard secondary syllabus`,
      `Curriculum covers the accelerated IP track, including topics not found in the standard secondary syllabus`,
      `A-Level preparation built in from IP Year 3, so students are ready well before JC`,
      `Teachers familiar with the specific demands of ${name}'s internal assessments and progression pathway`,
    ],
    programFacts: {
      eyebrow: 'The programme',
      title: 'What your child gets at DeepThink',
      body: `Our IP classes cover the accelerated curriculum, including advanced topics that go beyond the standard secondary syllabus.`,
      facts: [
        {
          label: 'Class size',
          value: CLASS_SIZE_VALUE,
          detail: 'IP-only, and grouped by learning pace',
        },
        { label: 'Weekly duration', value: '1.5 hours' },
        { label: 'Track', value: 'IP → A-Level pathway' },
        { label: 'A-Level prep', value: 'From IP Year 3' },
      ],
    },
    relatedLinks: {
      eyebrow: 'Explore related pages',
      title: 'More about IP and secondary Math at DeepThink',
      links: [
        {
          label: 'Integrated Programme',
          path: '/integrated-programme',
          description: "DeepThink's dedicated IP Math support",
        },
        {
          label: 'Secondary 1 (IP)',
          path: '/sec-1-ip',
          description: 'IP Year 1 — accelerated foundation',
        },
        {
          label: 'Secondary 2 (IP)',
          path: '/sec-2-ip',
          description: 'IP Year 2 — accelerated curriculum',
        },
        {
          label: 'Secondary 3 (IP)',
          path: '/sec-3-ip',
          description: 'IP Year 3 — A-Level preparation begins',
        },
        {
          label: 'Secondary 4 (IP)',
          path: '/sec-4-ip',
          description: 'IP Year 4 — JC readiness',
        },
        {
          label: 'A-Math',
          path: '/a-math',
          description: 'Advanced Mathematics bridging to A-Level',
        },
      ],
    },
    faqItems: [
      {
        question: `Does ${BRAND} have classes specifically for ${name} IP students?`,
        answer: `Yes. ${name} IP students are in a dedicated IP class. The content and pace are specific to the IP track.`,
      },
      {
        question: `How does the IP curriculum differ from the standard secondary syllabus?`,
        answer: `IP skips the national secondary examination (the Singapore-Cambridge SEC from 2027, which replaces O-Levels and N-Levels) and moves directly toward A-Levels. The Math content is more advanced and accelerated, covering topics like further algebra, calculus concepts, and proof techniques earlier than the standard secondary syllabus.`,
      },
      {
        question: `When is the right time for an IP student to start tuition?`,
        answer: `Many IP students benefit from starting in IP Year 1 or 2, before the pace accelerates. Students who join in Year 3 for A-Level preparation can also be accommodated.`,
      },
      {
        question: `Does ${BRAND} prepare IP students for JC?`,
        answer: `Yes. From IP Year 3 onwards, we incorporate A-Level preparation so students transition smoothly into JC without starting from scratch.`,
      },
    ],
  };
}

function ipIbContent(
  school: SchoolRecord
): Omit<SchoolPageContent, 'metaTitle' | 'metaDescription'> {
  const name = school.displayName;
  const isIntl = school.isInternational === true;

  return {
    heroHeading: `IB Math Tuition for ${withAbbr(school)} Students`,
    heroSubheading: isIntl
      ? `Dedicated IB Math classes for ${name} students — an international school in Singapore on the IB pathway.`
      : `Dedicated IB Math classes working on the IB Mathematics curriculum.`,
    levelBadge: 'IB',
    supportHeading: `How ${BRAND} supports ${name} IB students`,
    supportBullets: isIntl
      ? [
          `Dedicated IB Math class covering the IB Mathematics curriculum`,
          `As an international school, ${name} follows the IB pathway (MYP or IGCSE into the IB Diploma) — our class is matched to it, not the local MOE syllabus`,
          `Classes capped at ${MAX_CLASS_SIZE} students and grouped by learning pace, so your child gets genuine attention — every student's needs are taken care of, and no one gets left behind`,
          `Support from the pre-IB years through to the IB Diploma`,
        ]
      : [
          `Dedicated IB Math class covering the IB Mathematics curriculum`,
          `Lessons follow the IB Mathematics curriculum, not the local secondary syllabus`,
          `Classes capped at ${MAX_CLASS_SIZE} students and grouped by learning pace, so your child gets genuine attention — every student's needs are taken care of, and no one gets left behind`,
          `S1 to S4 coverage so your child can continue with ${BRAND} throughout their secondary years`,
        ],
    programFacts: {
      eyebrow: 'The programme',
      title: 'What your child gets at DeepThink',
      body: `Our dedicated IB class keeps students on the IB Mathematics curriculum throughout their secondary years.`,
      facts: [
        {
          label: 'Class size',
          value: CLASS_SIZE_VALUE,
          detail: 'IB-only, and grouped by learning pace',
        },
        { label: 'Weekly duration', value: '1.5 hours' },
        { label: 'Curriculum', value: 'IB Mathematics' },
        {
          label: 'Levels covered',
          value: isIntl ? 'Pre-IB to IB Diploma' : 'S1 – S4',
        },
      ],
    },
    relatedLinks: {
      eyebrow: 'Explore related pages',
      title: 'More about IB Math at DeepThink',
      links: [
        {
          label: 'IB Math',
          path: '/ib',
          description: "DeepThink's dedicated IB Math support",
        },
        {
          label: 'Secondary 1 (IB)',
          path: '/sec-1-ib',
          description: 'IB Year 1 — building toward the IB Math curriculum',
        },
        {
          label: 'Secondary 2 (IB)',
          path: '/sec-2-ib',
          description: 'IB Year 2 — IB-aligned content and methods',
        },
        {
          label: 'Secondary 3 (IB)',
          path: '/sec-3-ib',
          description: 'IB Year 3 — full IB curriculum begins',
        },
        {
          label: 'Secondary 4 (IB)',
          path: '/sec-4-ib',
          description: 'IB Year 4 — Diploma exam preparation',
        },
      ],
    },
    faqItems: [
      {
        question: `Does ${BRAND} have a class specifically for ${name}'s IB curriculum?`,
        answer: `Yes. ${name} students follow the IB Mathematics curriculum, and we run a dedicated IB Math class to match.`,
      },
      {
        question: `How does the IB Math curriculum differ from the local secondary syllabus?`,
        answer: `IB Math covers different content at a different pace from the MOE secondary syllabus. Students who attend a general secondary tuition class risk spending time on O-Level or SEC exam content that won't appear in their assessments. Our dedicated IB class avoids that.`,
      },
      {
        question: `When should an IB student start tuition?`,
        answer: `We recommend starting early — the IB curriculum diverges from the local secondary syllabus quickly, and gaps compound. Students who join mid-programme can also be accommodated.`,
      },
      {
        question: `What makes ${BRAND} different for IB students?`,
        answer: `A dedicated IB class that stays on the IB curriculum throughout your child's secondary years.`,
      },
    ],
  };
}

function mixedContent(
  school: SchoolRecord
): Omit<SchoolPageContent, 'metaTitle' | 'metaDescription'> {
  const name = school.displayName;

  return {
    heroHeading: `Math Tuition for ${withAbbr(school)} Students`,
    heroSubheading: `Primary and Secondary Math tuition for ${name} students at every level, at one centre.`,
    levelBadge: 'Primary & Secondary',
    supportHeading: `How ${BRAND} supports ${name} students`,
    supportBullets: [
      `P1 to P6 primary classes and S1 to S4/S5 secondary classes, all in one place`,
      `Secondary classes grouped by stream (G1, G2, G3), covering the exact content for each`,
      `One centre for siblings at different levels — no juggling multiple providers`,
      `Consistent teaching methods from primary to secondary so your child's approach carries through`,
    ],
    programFacts: {
      eyebrow: 'The programme',
      title: 'What your child gets at DeepThink',
      body: `${name} runs from primary through secondary. We cover every level at one centre, with stream-specific secondary classes from S1 onwards.`,
      facts: [
        {
          label: 'Class size',
          value: CLASS_SIZE_VALUE,
          detail: 'Level and stream-specific, and grouped by learning pace',
        },
        { label: 'Weekly duration', value: '1.5 hours' },
        { label: 'Primary levels', value: 'P1 – P6' },
        { label: 'Secondary streams', value: 'G1, G2, G3 (separate)' },
      ],
    },
    relatedLinks: {
      eyebrow: 'Explore by level',
      title: 'Find the right class for your child',
      links: [
        {
          label: 'Primary School Math',
          path: '/primary-school',
          description: 'Overview of P1–P6 support',
        },
        {
          label: 'Secondary School Math',
          path: '/secondary-school',
          description: 'Overview of S1–S4/S5 support',
        },
        {
          label: 'Primary 1',
          path: '/primary-1',
          description: 'Number sense and early problem-solving',
        },
        {
          label: 'Primary 2',
          path: '/primary-2',
          description: 'Fractions, measurement, and word problems',
        },
        {
          label: 'Primary 3',
          path: '/primary-3',
          description: 'Geometry, multiplication, and division',
        },
        {
          label: 'Primary 4',
          path: '/primary-4',
          description: 'Fractions, decimals, area, and perimeter',
        },
        {
          label: 'Primary 5',
          path: '/primary-5',
          description: 'Percentages, rate, and the start of PSLE prep',
        },
        {
          label: 'Primary 6',
          path: '/primary-6',
          description: 'Full PSLE preparation and exam technique',
        },
        {
          label: 'Secondary 1',
          path: '/sec-1',
          description: 'Algebra foundations and geometric reasoning',
        },
        {
          label: 'Secondary 2',
          path: '/sec-2',
          description: 'Simultaneous equations, indices, and statistics',
        },
        {
          label: 'Secondary 3',
          path: '/sec-3',
          description: 'Stream-specific O-Level and SEC exam prep begins',
        },
        {
          label: 'Secondary 4',
          path: '/sec-4',
          description: 'Full O-Level, N-Level, and SEC exam preparation',
        },
        {
          label: 'PSLE Math',
          path: '/psle',
          description: 'Dedicated PSLE preparation',
        },
        {
          label: 'O-Level / SEC Math',
          path: '/o-level',
          description: 'O-Level and SEC exam preparation',
        },
        {
          label: 'E-Math',
          path: '/e-math',
          description: 'Elementary Mathematics',
        },
        {
          label: 'A-Math',
          path: '/a-math',
          description: 'Additional Mathematics',
        },
      ],
    },
    faqItems: [
      {
        question: `Does ${BRAND} have classes for both primary and secondary ${name} students?`,
        answer: `Yes. We support ${name} students from P1 through P6, and S1 through S4/S5 in stream-specific classes (G1, G2, G3).`,
      },
      {
        question: `Can siblings at different levels attend the same tuition centre?`,
        answer: `Yes. Primary and secondary siblings can both attend ${BRAND}, each in the appropriate class for their level.`,
      },
      {
        question: `What happens when my primary school child moves into secondary school?`,
        answer: `We make the transition seamless. Students who complete P6 with us can continue directly into our secondary programme, and their teachers will already know their strengths and gaps.`,
      },
      {
        question: `What makes ${BRAND} different?`,
        answer: `Small classes, level and stream separation, and consistent teaching methods from primary through secondary.`,
      },
    ],
  };
}

// --- Meta content helpers ---

/**
 * Roughly what Google renders of a description before truncating. Used as a
 * budget rather than a hard rule: the cut-off figures are always kept and the
 * tuition clause shortens around them.
 */
const META_DESCRIPTION_BUDGET = 165;

/**
 * Longest school name a cut-off-led title or description keeps its abbreviation
 * on. Past this, the parenthetical pushes "Cut-Off Point" — the phrase the
 * searcher typed — past where Google truncates the title, and in the
 * description it crowds out the tuition clause. Some names are already
 * parenthesised ("Fairfield Methodist School (Secondary)"), so appending the
 * abbreviation there reads as a doubled parenthetical anyway.
 */
const CUT_OFF_ENTITY_BUDGET = 34;

/** School name for cut-off meta, with the abbreviation only where it fits. */
function cutOffEntityName(school: SchoolRecord): string {
  const withAbbreviation = withAbbr(school);
  return withAbbreviation.length <= CUT_OFF_ENTITY_BUDGET
    ? withAbbreviation
    : school.displayName;
}

/** The tuition promise, kept in a cut-off-led title so it still matches the page. */
function tuitionQualifier(variant: SchoolVariant): string {
  switch (variant) {
    case 'ip':
      return 'IP Math Tuition';
    case 'ip-ib':
      return 'IB Math Tuition';
    default:
      return 'Math Tuition';
  }
}

function buildMetaTitle(
  school: SchoolRecord,
  variant: SchoolVariant,
  hasCutOff: boolean
): string {
  const name = withAbbr(school);
  // Schools we hold a verified cut-off for lead on it: the page ranks on the
  // first page for '<school> cut off point' and the old title promised nothing
  // the searcher asked for. The tuition qualifier stays so the title still
  // describes the whole page.
  if (hasCutOff) {
    return `${cutOffEntityName(school)} Cut-Off Point & ${tuitionQualifier(variant)} | ${BRAND}`;
  }
  switch (variant) {
    case 'ip-ib':
      return `IB Math Tuition for ${name} Students | ${BRAND}`;
    case 'ip':
      return `IP Math Tuition for ${name} Students | ${BRAND}`;
    default:
      return `${name} Math Tuition | ${BRAND}`;
  }
}

/**
 * Last resort, short enough that even the longest school name in the dataset
 * still keeps a tuition promise in its description.
 */
const MINIMAL_TUITION_CLAUSE = 'Plus Math tuition.';

/**
 * Tuition clauses for a cut-off-led description, longest first. The first one
 * that fits the budget alongside the figures wins, so an affiliated school with
 * a long name still keeps a tuition promise instead of losing it to truncation.
 */
function cutOffTuitionClauses(
  school: SchoolRecord,
  variant: SchoolVariant
): string[] {
  const name = school.abbreviation || school.displayName;
  const byVariant = (): string[] => {
    switch (variant) {
      case 'ip':
        return [
          `Plus IP Math tuition for ${name} students — accelerated curriculum, A-Level preparation.`,
          'Plus IP Math tuition, accelerated curriculum.',
          'Plus IP Math tuition.',
        ];
      case 'ip-ib':
        return [
          `Plus IB Math tuition for ${name} students — a dedicated IB Mathematics class.`,
          'Plus IB Math tuition, a dedicated IB class.',
          'Plus IB Math tuition.',
        ];
      case 'mixed':
        return [
          `Plus Primary and Secondary Math tuition for ${name} students, level and stream-specific.`,
          'Plus Primary and Secondary Math tuition.',
        ];
      default:
        return [
          `Plus Secondary Math tuition for ${name} students — G1, G2 and G3 in separate classes.`,
          'Plus Secondary Math tuition, G1/G2/G3 in separate classes.',
          'Plus Secondary Math tuition.',
        ];
    }
  };
  return [...byVariant(), MINIMAL_TUITION_CLAUSE];
}

function buildCutOffMetaDescription(
  school: SchoolRecord,
  variant: SchoolVariant,
  summary: string
): string {
  const base = `${cutOffEntityName(school)} PSLE cut-off points, ${CUT_OFF_S1_INTAKE_YEAR} S1 intake (MOE): ${summary}.`;
  for (const clause of cutOffTuitionClauses(school, variant)) {
    const candidate = `${base} ${clause}`;
    if (candidate.length <= META_DESCRIPTION_BUDGET) return candidate;
  }
  return base;
}

function buildMetaDescription(
  school: SchoolRecord,
  variant: SchoolVariant
): string {
  const name = withAbbr(school);
  switch (variant) {
    case 'primary':
      return `Expert Primary Math tuition for ${name} students (P1–P6). Small classes, MOE syllabus-aligned, PSLE-focused. Book a free trial.`;
    case 'secondary':
      return `Secondary Math tuition for ${name} students. G1, G2, G3 in separate classes. O-Level, SEC exam, and N-Level preparation. Book a free trial.`;
    case 'ip':
      return `IP Math tuition for ${name} students. Dedicated IP class, accelerated curriculum, A-Level preparation. Book a free trial.`;
    case 'ip-ib':
      return school.isInternational
        ? `IB Math tuition for ${name} students — an international school in Singapore on the IB pathway (MYP/IGCSE → IB Diploma). Dedicated IB Math class. Book a free trial.`
        : `IB Math tuition for ${name} students. Dedicated IB class for Analysis and Approaches / Applications and Interpretation. Book a free trial.`;
    case 'mixed':
      return `Math tuition for ${name} students — Primary and Secondary. Level and stream-specific classes. Book a free trial.`;
  }
}

// --- Heritage integration ---

function buildIntroText(
  school: SchoolRecord,
  variant: SchoolVariant,
  heritage: SchoolHeritage
): string {
  const name = school.displayName;
  const parts: string[] = [];

  // Opening 1-2 sentences from heritage blurb
  if (heritage.heritage) {
    const sentences = heritage.heritage.match(/[^.!?]+[.!?]+/g);
    if (sentences) {
      // Take up to 2 sentences, capped at ~250 chars
      let intro = sentences[0].trim();
      if (sentences[1] && intro.length + sentences[1].length < 250) {
        intro += ' ' + sentences[1].trim();
      }
      parts.push(intro);
    }
  }

  // Founding year if not already mentioned in the heritage sentences
  if (
    heritage.foundingYear &&
    !parts[0]?.includes(String(heritage.foundingYear))
  ) {
    parts.push(`The school was founded in ${heritage.foundingYear}.`);
  }

  // Motto
  if (heritage.motto) {
    parts.push(
      `With a motto of "${heritage.motto}", ${name} has a distinctive educational identity.`
    );
  }

  // Mission/vision — wrap in a proper sentence
  if (heritage.mission) {
    const m = heritage.mission.trim();
    // Strip leading "to " if present, then lowercase the first char for "mission is to ..."
    const stripped = m.toLowerCase().startsWith('to ') ? m.slice(3) : m;
    const lower = stripped.charAt(0).toLowerCase() + stripped.slice(1);
    parts.push(`The school's mission is to ${lower}.`.replace('..', '.'));
  } else if (heritage.vision) {
    const v = heritage.vision.trim();
    const lower = v.charAt(0).toLowerCase() + v.slice(1);
    parts.push(`The school's vision is to be ${lower}.`.replace('..', '.'));
  }

  // Tuition connection based on variant
  const variantLine = (() => {
    switch (variant) {
      case 'primary':
        return `${BRAND}'s Primary Math programme is designed to support ${name} students from P1 through P6.`;
      case 'secondary':
        return `${BRAND}'s Secondary Math programme supports ${name} students with stream-specific classes for G1, G2, and G3.`;
      case 'ip':
        return `${BRAND} offers dedicated IP Math classes to support ${name} students on the accelerated track.`;
      case 'ip-ib':
        return `${BRAND} offers dedicated IB Math classes matched to the curriculum ${name} students follow.`;
      case 'mixed':
        return `${BRAND} supports ${name} students across both Primary and Secondary levels.`;
    }
  })();
  parts.push(variantLine);

  return parts.join(' ');
}

function buildAboutSchool(
  school: SchoolRecord,
  heritage: SchoolHeritage
): SchoolAboutContent {
  const facts: { label: string; value: string }[] = [];

  if (heritage.foundingYear) {
    facts.push({ label: 'Founded', value: String(heritage.foundingYear) });
  }
  if (heritage.motto) {
    facts.push({ label: 'Motto', value: heritage.motto });
  }
  if (heritage.vision) {
    facts.push({ label: 'Vision', value: heritage.vision });
  }
  if (heritage.mission) {
    facts.push({ label: 'Mission', value: heritage.mission });
  }
  if (heritage.values?.length) {
    facts.push({ label: 'Values', value: heritage.values.join(', ') });
  }

  return {
    heading: `About ${school.displayName}`,
    body: heritage.heritage || '',
    facts,
  };
}

// --- Public API ---

export function buildSchoolPageContent(
  school: SchoolRecord,
  variant: SchoolVariant,
  heritage?: SchoolHeritage | null
): SchoolPageContent {
  const variantContent = (() => {
    switch (variant) {
      case 'primary':
        return primaryContent(school);
      case 'secondary':
        return secondaryContent(school);
      case 'ip':
        return ipContent(school);
      case 'ip-ib':
        return ipIbContent(school);
      case 'mixed':
        return mixedContent(school);
    }
  })();

  // MOE publishes a cut-off point for 139 of the secondary schools we cover and
  // for none of the others. `cutOffs` absent means "MOE published nothing" — the
  // meta stays on the tuition template and the page renders no section.
  const cutOffs = getSchoolCutOffPoints(school.slug);
  const cutOffSummary =
    cutOffs && getHeadlineCutOff(cutOffs)
      ? buildCutOffMetaSummary(cutOffs)
      : null;

  const base: SchoolPageContent = {
    metaTitle: buildMetaTitle(school, variant, cutOffSummary !== null),
    metaDescription: cutOffSummary
      ? buildCutOffMetaDescription(school, variant, cutOffSummary)
      : buildMetaDescription(school, variant),
    ...variantContent,
    // Class size is the question parents ask first, so every school page
    // answers it — appended here rather than repeated in all five variants.
    faqItems: [...variantContent.faqItems, CLASS_SIZE_FAQ],
  };

  const cutOffContent = buildSchoolCutOffContent(
    school.slug,
    school.displayName,
    variant
  );
  if (cutOffContent) base.cutOffPoints = cutOffContent;

  // Enrich with heritage data if available and reviewed
  if (heritage && !heritage.needsReview) {
    base.introText = buildIntroText(school, variant, heritage);
    if (
      heritage.heritage ||
      heritage.foundingYear ||
      heritage.motto ||
      heritage.vision ||
      heritage.mission ||
      heritage.values?.length
    ) {
      base.aboutSchool = buildAboutSchool(school, heritage);
    }
  }

  const override = getSchoolOverride(school.slug);
  return override ? applySchoolOverride(base, override) : base;
}
